// §4 S2 — Materials tab (Module 04). The pinned BOM's plan vs what has
// actually been issued, per component (§2.5's `WorkOrderMaterialLine`), the
// office-side manual ISSUE, and — since slice 04-B-4 (D-076/D-081) — the
// RETURN action, which REPLACED the "returns arrive with 04-B" pending note:
// each §2.5 issue row with an unreturned remainder offers Return, and the
// request names that row (`reversal_of_issue_id`, qty negative, the row's own
// UoM — the §5 row 30 shape the server validates).
//
// ONE DELIBERATE NARROWING, stated rather than drawn (D-074): the issue AND
// return buttons are HIDDEN FROM PLANNER — `production.material.issue` is
// storekeeper / facility_admin / group_admin (§7); the mirror decides, this
// file only consumes `canIssue` and `canReturn`.
//
// ISSUE AND RETURN ARE TWO GATES SINCE D-308 (2026-09-27). e30's issue branch
// acts on a released or in-progress order and its return branch also on a
// COMPLETED one, because a crew carries leftover copper back after the last
// step and the order takes it until it is closed. So a completed order offers
// Return and no Issue, with a hint saying why, and a closed order offers
// neither. The screen decides each from e30's own arm (work-order-detail.tsx).
// A return pressed on a page that went stale while finance closed the order is
// refused 409 `WO_CLOSED_IMMUTABLE` with `way_on: inventory_stock_adjustment`,
// and that refusal is worded HERE, at the tab, from the `way_on` key and in
// the reader's language: the detail refetches on it, the order reads closed,
// the Return control and its form go away, and a sentence inside the form
// would go with them.
//
// A CLOSED ORDER SAYS WHERE LEFTOVER MATERIAL GOES (the D-308 review). The
// refused return above is not the only way a reader meets a closed order: a
// fresh render offers no Return, and a window-focus refetch that finds the
// order closed takes an open Return form away. So a holder of the code, on a
// closed order with material still out, reads a standing note naming the
// stock adjustment. The refusal's own sentence replaces it rather than
// repeating it.
//
// THE KEY RIDES THE INTENT (the same review). Each form mints its
// `Idempotency-Key` per attempt with `useIntentKey`, so a return or an issue
// whose response was lost replays when pressed again instead of posting twice.
//
// `issued_cost_minor` / `unit_cost_minor` render only when the key is PRESENT
// (§7 redaction-by-absence): for a caller without `production.view_costs` the
// column does not exist — it is never dashed and never 0.

import { useMemo, useState, type ReactNode } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { StatusChip } from "../../../../components/status-chip";
import { RequestError } from "../../../../lib/request";
import { useTerm } from "../../../onboarding/hooks/terminology";
import {
  useCreateMaterialIssue,
  useProductIndex,
  useUoms,
  type MaterialIssue,
  type MaterialIssueCreateRequest,
  type WorkOrderMaterialLine,
  type WorkOrderOperation,
} from "../hooks/production-queries";
import { useIntentKey } from "../hooks/intent-key";
import {
  formatMinor,
  formatQty,
  formatTimestamp,
  isClosedReturnRefusal,
  isValidQtyInput,
  productionErrorMessage,
  qtyIsNonZero,
  WAY_ON_STOCK_ADJUSTMENT,
  wayOnAdjustSearch,
} from "../lib/format";
import { AdjustStockLink } from "../../inventory/components/adjust-stock-link";
import { exactRemainder } from "../../inventory/lib/measure";
import { RefLabel } from "./ref-label";
import { shortId } from "../../../../lib/short-id";

/** An issue row's unreturned remainder, from the rows themselves (`qty` is a
 * ≤3dp decimal string; the SERVER re-validates exactly — this number drives
 * the button and the default, never the ledger). */
export function returnableOf(issue: MaterialIssue, all: readonly MaterialIssue[]): number {
  if (Number(issue.qty) <= 0) return 0;
  const returned = all
    .filter((row) => row.reversal_of_issue_id === issue.id)
    .reduce((sum, row) => sum + Math.abs(Number(row.qty)), 0);
  const remaining = Number(issue.qty) - returned;
  return remaining > 0 ? Number(remaining.toFixed(3)) : 0;
}

/** The spec 06 §4.12 search for one unreturned issue row (D-315): its
 * component, its batch, the container it drew (D-308 round 2 decides in the
 * form whether its leftover can come back loose), and what is still out as a
 * hint — computed exactly, never typed in for the person — booked back out of
 * work in progress on the order's plant. */
export function adjustSearchForIssue(
  issue: MaterialIssue,
  all: readonly MaterialIssue[],
  workOrderId: string,
  facilityId: string | undefined,
) {
  const returns = all.filter((row) => row.reversal_of_issue_id === issue.id).map((row) => row.qty);
  return wayOnAdjustSearch({
    workOrderId,
    facilityId,
    productId: issue.component_product_id,
    lotId: issue.lot_id,
    unitId: issue.stock_unit_id,
    expectedQty: exactRemainder(issue.qty, returns),
    uom: issue.uom,
    direction: "increase",
    reason: "found",
    from: "work_order",
  });
}

export function MaterialsTab({
  workOrderId,
  lines,
  issues,
  operations,
  canIssue,
  canReturn,
  returnsOnly = false,
  closedToReturns = false,
  currency,
  facilityId,
}: {
  workOrderId: string;
  lines: readonly WorkOrderMaterialLine[];
  /** Every §2.5 row (issues AND returns), oldest first — §5 row 3's
   * `material_issues[]` (added 2026-08-05). */
  issues: readonly MaterialIssue[];
  operations: readonly WorkOrderOperation[];
  /** e30's ISSUE branch: a released or in-progress order, and the code. */
  canIssue: boolean;
  /** e30's RETURN branch: a released, in-progress or completed order, and
   * the code (D-308). */
  canReturn: boolean;
  /** The order is completed: it takes leftover material back until it is
   * closed and takes no further issue, and the tab says so (D-308). */
  returnsOnly?: boolean;
  /** The order is closed and the reader holds the code that would return
   * material: the tab names the stock adjustment as the way on for anything
   * still out (the D-308 review). */
  closedToReturns?: boolean;
  /** The WO header's currency (§9.13 fixes it at creation) — lines carry
   * minor units only. */
  currency: string;
  /** The order's plant — the stock adjustment link opens on it (D-315). */
  facilityId?: string | undefined;
}) {
  const { t, i18n } = useTranslation();
  const { term } = useTerm();
  const products = useProductIndex([
    ...lines.map((line) => line.component_product_id),
    ...issues.map((row) => row.component_product_id),
  ]);
  const [issueOpen, setIssueOpen] = useState(false);
  // e30's closed-order refusal of a return, held at the TAB (D-308): it
  // outlives the form, which closes once the refetched order reads closed.
  const [closedRefusal, setClosedRefusal] = useState(false);

  // §7 layer 2: the key actually served, on any line.
  const showCost = lines.some((line) => line.issued_cost_minor !== undefined);

  // THE WAY ON, LINKED (D-315). Both D-308 sentences below name a stock
  // adjustment in Inventory; for a holder of `inventory.adjust` they now open
  // it. One unreturned row fills in its component, batch and container, with
  // what is still out as a hint; with several, the sentence opens the form on
  // the plant and each row in the history carries its own link.
  const unreturned = issues.filter((row) => returnableOf(row, issues) > 0);
  const onlyUnreturned = unreturned.length === 1 ? unreturned[0] : undefined;
  const wayOnSearch =
    onlyUnreturned !== undefined
      ? adjustSearchForIssue(onlyUnreturned, issues, workOrderId, facilityId)
      : wayOnAdjustSearch({
          workOrderId,
          facilityId,
          direction: "increase",
          reason: "found",
          from: "work_order",
        });
  // The link sits right AFTER the sentence, not inside it: the sentence is
  // the tab's own words and keeps its exact text for every reader (M04-S19
  // reads it whole), and the link is an addition only some readers get.
  const wayOnLink = (testId: string) => (
    <AdjustStockLink
      search={wayOnSearch}
      label={t("inventory.adjust.fromProduction")}
      testId={testId}
    />
  );

  // A component the product index has not resolved is NOT its uuid. The
  // history column printed `0191f3c1-8a4e-…` where a material name belongs,
  // and printed exactly the same thing whether the index was still in flight
  // or the id genuinely does not resolve (builder report, 2026-08-12).
  const productRef = (id: string): ReactNode => {
    const product = products.byId.get(id);
    return (
      <RefLabel
        value={product === undefined ? undefined : `${product.sku} · ${product.name}`}
        id={id}
        pending={products.isPending}
      />
    );
  };

  const lineColumns: DataTableColumn<WorkOrderMaterialLine>[] = [
    {
      key: "component_product_id",
      header: term("material"),
      id: true,
      render: (line) => {
        const product = products.byId.get(line.component_product_id);
        return product !== undefined ? `${product.sku} · ${product.name}` : "";
      },
    },
    {
      key: "qty_planned",
      header: t("production.detail.materials.colPlanned"),
      numeric: true,
      render: (line) => `${formatQty(line.qty_planned, { locale: i18n.language })} ${line.uom}`,
    },
    {
      // What module 06 holds for the line, in the component's base unit, with
      // the part of a live order's plan that is neither issued nor held marked
      // "Not reserved" (spec 04 §3.5, D-405 ruling 7).
      key: "qty_reserved_base",
      header: t("production.detail.materials.colReserved"),
      numeric: true,
      render: (line) => (
        <span className="wo-material-reserved">
          <span>{`${formatQty(line.qty_reserved_base, { locale: i18n.language })} ${line.base_uom}`}</span>
          {qtyIsNonZero(line.qty_not_reserved_base) ? (
            <StatusChip
              variant="warn"
              data-testid="wo-material-not-reserved"
              data-status="not_reserved"
            >
              {t("production.detail.materials.notReserved", {
                qty: formatQty(line.qty_not_reserved_base, { locale: i18n.language }),
                uom: line.base_uom,
              })}
            </StatusChip>
          ) : null}
        </span>
      ),
    },
    {
      key: "qty_issued",
      header: t("production.detail.materials.colIssued"),
      numeric: true,
      render: (line) => `${formatQty(line.qty_issued, { locale: i18n.language })} ${line.uom}`,
    },
    {
      key: "qty_variance",
      header: t("production.detail.materials.colVariance"),
      numeric: true,
      render: (line) => `${formatQty(line.qty_variance, { locale: i18n.language })} ${line.uom}`,
    },
    // §7 redaction-by-absence: the column does not EXIST for a caller without
    // `production.view_costs` — it is never dashed and never 0.
    ...(showCost
      ? [
          {
            key: "issued_cost_minor",
            header: t("production.detail.materials.colCost"),
            numeric: true,
            render: (line: WorkOrderMaterialLine) =>
              line.issued_cost_minor !== undefined
                ? formatMinor(line.issued_cost_minor, currency, i18n.language)
                : "",
          },
        ]
      : []),
  ];

  if (lines.length === 0 && issues.length === 0) {
    return (
      <div className="wo-panel-materials">
        <p className="production-hint">{t("production.detail.materials.empty")}</p>
      </div>
    );
  }

  return (
    <div className="wo-panel-materials">
      {closedRefusal ? (
        // The refusal's WAY ON, worded from `way_on` (D-308). The server's
        // `detail` is the English fallback and is never shown here.
        <p
          className="form-error"
          role="alert"
          data-testid="material-return-closed"
          data-way-on={WAY_ON_STOCK_ADJUSTMENT}
        >
          {t("production.detail.materials.returnClosedWayOn")}
        </p>
      ) : null}
      {closedRefusal ? wayOnLink("material-return-closed-adjust") : null}
      {returnsOnly && canReturn ? (
        <p className="production-note" role="note" data-testid="material-return-after-completion">
          {t("production.detail.materials.returnAfterCompletionHint")}
        </p>
      ) : null}
      {closedToReturns && !closedRefusal && unreturned.length > 0 ? (
        // Links to spec 06 §4.12 for a holder of `inventory.adjust`, as the
        // refusal's sentence does (D-315); anyone else reads it unlinked.
        <p
          className="production-note"
          role="note"
          data-testid="material-return-after-close"
          data-way-on={WAY_ON_STOCK_ADJUSTMENT}
        >
          {t("production.detail.materials.returnAfterCloseHint")}
        </p>
      ) : null}
      {closedToReturns && !closedRefusal && unreturned.length > 0
        ? wayOnLink("material-return-after-close-adjust")
        : null}
      {lines.some((line) => qtyIsNonZero(line.qty_not_reserved_base)) ? (
        // Visible text, not a hover title: a touch screen has no hover, and a
        // warn chip with no reason is a question nobody on a tablet can ask
        // (D-407 ruling 7). Shown once, above the table, while any line
        // carries the chip.
        <p className="production-note" role="note" data-testid="wo-material-not-reserved-hint">
          {t("production.detail.materials.notReservedHint")}
        </p>
      ) : null}
      {canIssue ? (
        <div className="wo-transitions">
          <Button
            onClick={() => {
              setIssueOpen((prev) => !prev);
            }}
          >
            {t("production.detail.materials.issue")}
          </Button>
        </div>
      ) : null}

      {issueOpen && canIssue ? (
        <IssueForm
          workOrderId={workOrderId}
          lines={lines}
          operations={operations}
          // An <option> holds TEXT, so this one stays a string — but an
          // unresolved component still shows a short id rather than a full
          // uuid nobody can read past in a select.
          productName={(id) => products.byId.get(id)?.name ?? shortId(id)}
          onDone={() => {
            setIssueOpen(false);
          }}
        />
      ) : null}

      {lines.length > 0 ? (
        <DataTable
          columns={lineColumns}
          rows={lines}
          rowKey={(line) =>
            `${line.component_product_id}:${line.bom_line_id ?? ""}:${line.wo_operation_id ?? ""}`
          }
          empty={null}
          // A caption where — and only where — nothing visible names the table.
          // The plan/issued table sits in a tab PANEL with no heading of its
          // own, so the tab's word is the only name it has; the history table
          // below carries an <h4> and would only echo it.
          caption={t("production.detail.tab.materials")}
          rowProps={(line) => ({
            "data-testid": `material-line-${line.component_product_id}`,
          })}
        />
      ) : null}

      <IssueHistory
        workOrderId={workOrderId}
        issues={issues}
        canReturn={canReturn}
        currency={currency}
        productName={productRef}
        adjustRows={closedToReturns || closedRefusal}
        facilityId={facilityId}
        onReturned={() => {
          setClosedRefusal(false);
        }}
        onClosedRefusal={() => {
          setClosedRefusal(true);
        }}
      />
    </div>
  );
}

/**
 * The §2.5 rows, oldest first, with the RETURN action on every issue that
 * still has an unreturned remainder (04-B-4 — D-076/D-081; this section
 * REPLACED the stated-pending note). Return rows render as history: the
 * ledger is append-only, so the record shows both directions rather than
 * netting them away.
 */
function IssueHistory({
  workOrderId,
  issues,
  canReturn,
  currency,
  productName,
  adjustRows,
  facilityId,
  onReturned,
  onClosedRefusal,
}: {
  workOrderId: string;
  issues: readonly MaterialIssue[];
  canReturn: boolean;
  currency: string;
  productName: (id: string) => ReactNode;
  /** The order takes nothing back (closed): each row with a remainder links
   * to spec 06 §4.12 instead (D-315). */
  adjustRows: boolean;
  facilityId: string | undefined;
  onReturned: () => void;
  onClosedRefusal: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [returning, setReturning] = useState<MaterialIssue | undefined>(undefined);

  const showCost = issues.some((row) => row.unit_cost_minor !== undefined);

  const columns: DataTableColumn<MaterialIssue>[] = [
    {
      key: "issued_at",
      header: t("production.detail.materials.colWhen"),
      id: true,
      render: (row) => (
        <>
          {/* lib/datetime's one formatter (F9): `toLocaleString` renders a THIRD
              shape ("10 Aug 2026, 13:33:23") next to `formatTimestamp`'s
              elsewhere on this screen. */}
          <span>{formatTimestamp(row.issued_at, i18n.language)}</span>
          <span className="production-hint"> {productName(row.component_product_id)}</span>
          {Number(row.qty) < 0 ? (
            <span className="production-hint"> — {t("production.detail.materials.returnRow")}</span>
          ) : null}
        </>
      ),
    },
    {
      key: "issue_method",
      header: t("production.detail.materials.colMethod"),
      render: (row) => t(`production.detail.materials.methodLabel.${row.issue_method}`),
    },
    {
      key: "qty",
      header: t("production.detail.materials.qty"),
      numeric: true,
      render: (row) => `${formatQty(row.qty, { locale: i18n.language })} ${row.uom}`,
    },
    {
      key: "remaining",
      header: t("production.detail.materials.colRemaining"),
      numeric: true,
      render: (row) =>
        Number(row.qty) < 0
          ? ""
          : `${formatQty(String(returnableOf(row, issues)), { locale: i18n.language })} ${row.uom}`,
    },
    ...(showCost
      ? [
          {
            key: "unit_cost_minor",
            header: t("production.detail.materials.colCost"),
            numeric: true,
            render: (row: MaterialIssue) =>
              row.unit_cost_minor !== undefined
                ? formatMinor(row.unit_cost_minor, currency, i18n.language)
                : "",
          },
        ]
      : []),
    // D-315: on a closed order a row's remainder comes back through a
    // stock adjustment, one link per row, since the rows can differ in
    // component, batch and unit. AdjustStockLink renders nothing for a reader
    // without `inventory.adjust`.
    // Only when MORE than one row is still out (spec 04 S2): with one, the
    // sentence above already carries that row whole, and a second identical
    // link in the history would be noise.
    ...(adjustRows && !canReturn && issues.filter((row) => returnableOf(row, issues) > 0).length > 1
      ? [
          {
            key: "adjust",
            header: t("production.common.actions"),
            actions: true,
            render: (row: MaterialIssue) =>
              returnableOf(row, issues) > 0 ? (
                <AdjustStockLink
                  search={adjustSearchForIssue(row, issues, workOrderId, facilityId)}
                  label={t("inventory.adjust.fromProduction")}
                  testId={`material-adjust-${row.id}`}
                />
              ) : null,
          },
        ]
      : []),
    ...(canReturn
      ? [
          {
            // The header used to be an empty `<th/>`; the primitive's collapse
            // turns a header into the phone card's field label, so the column
            // is NAMED now — an unnamed one would print nothing above a button.
            key: "actions",
            header: t("production.common.actions"),
            actions: true,
            render: (row: MaterialIssue) =>
              returnableOf(row, issues) > 0 ? (
                <Button
                  data-testid={`material-return-${row.id}`}
                  onClick={() => {
                    setReturning(row);
                  }}
                >
                  {t("production.detail.materials.returnAction")}
                </Button>
              ) : null,
          },
        ]
      : []),
  ];

  return (
    <section className="materials-history" data-testid="material-issues-history">
      <h4>{t("production.detail.materials.historyTitle")}</h4>
      {issues.length === 0 ? (
        <p className="production-hint">{t("production.detail.materials.historyEmpty")}</p>
      ) : (
        <DataTable
          columns={columns}
          rows={issues}
          rowKey={(row) => row.id}
          empty={null}
          rowProps={(row) => ({ "data-testid": `material-issue-${row.id}` })}
          // THE RETURN FORM IS ATTACHED TO THE ROW IT REVERSES (the §3.3
          // `expansion` contract). It used to float below the whole table, and
          // with a dozen issue rows listed the reader had to reconstruct which
          // one the form was about — from a form whose whole meaning is "this
          // issue" (`reversal_of_issue_id`).
          // `canReturn` gates the form as well as its button: when the order
          // stops taking returns under an open form (finance closed it), the
          // refetched order must not leave a form that can only be refused.
          expansion={(row) =>
            canReturn && returning?.id === row.id ? (
              <ReturnForm
                workOrderId={workOrderId}
                issue={row}
                remaining={returnableOf(row, issues)}
                onDone={() => {
                  setReturning(undefined);
                }}
                onReturned={onReturned}
                onClosedRefusal={() => {
                  setReturning(undefined);
                  onClosedRefusal();
                }}
              />
            ) : null
          }
          expansionLabel={() => t("production.detail.materials.returnTitle")}
        />
      )}
    </section>
  );
}

/**
 * The sentence for a refused return. Module 06's 422 `UNIT_NOT_AVAILABLE` gets
 * the return's own sentence (the D-308 review): D-308 round 2 keeps a
 * quarantined or in-transit container named on purpose, so a storekeeper
 * returning onto a held drum meets it, and module 06's title is English, cites
 * spec and decision numbers, names a field she cannot set and advises the bulk
 * path the ruling rejects for a held drum. The same code also answers a drum
 * drained between e30's read and module 06's lock, which a second press returns
 * as bulk, so the sentence covers both. Everything else is the area's mapping.
 */
function returnErrorMessage(t: TFunction, error: unknown): string {
  if (error instanceof RequestError && error.code === "UNIT_NOT_AVAILABLE") {
    return t("production.detail.materials.returnUnitNotAvailable");
  }
  return productionErrorMessage(t, error);
}

/** §5 row 30's NEGATIVE branch: qty `-{magnitude}`, the original's UoM and
 * component, `reversal_of_issue_id` naming the row (04-B-4 — D-076/D-081). */
function ReturnForm({
  workOrderId,
  issue,
  remaining,
  onDone,
  onReturned,
  onClosedRefusal,
}: {
  workOrderId: string;
  issue: MaterialIssue;
  remaining: number;
  onDone: () => void;
  onReturned: () => void;
  /** e30 refused because the order closed under the form (D-308). The tab
   * words it, since this form is about to go away. */
  onClosedRefusal: () => void;
}) {
  const { t } = useTranslation();
  const returnIssue = useCreateMaterialIssue();
  const intent = useIntentKey();
  const [qty, setQty] = useState(String(remaining));
  const [validationKey, setValidationKey] = useState<string | undefined>(undefined);

  return (
    <form
      className="production-panel"
      aria-label={t("production.detail.materials.returnTitle")}
      data-testid="material-return-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (!isValidQtyInput(qty, { positive: true })) {
          setValidationKey("production.detail.materials.qtyPositive");
          return;
        }
        // A client-side courtesy only — the SERVER holds the real
        // MATERIAL_RETURN_EXCEEDS_ISSUED guard.
        if (Number(qty) > remaining) {
          setValidationKey("production.detail.materials.returnExceeds");
          return;
        }
        setValidationKey(undefined);
        const body: MaterialIssueCreateRequest = {
          component_product_id: issue.component_product_id,
          qty: `-${qty.trim()}`,
          uom: issue.uom,
          reversal_of_issue_id: issue.id,
        };
        returnIssue.mutate(
          { workOrderId, body, idempotencyKey: intent.keyFor(JSON.stringify(body)) },
          {
            onSuccess: () => {
              intent.settle();
              onReturned();
              onDone();
            },
            onError: (error) => {
              intent.settle(error);
              if (isClosedReturnRefusal(error)) onClosedRefusal();
            },
          },
        );
      }}
    >
      <h4>{t("production.detail.materials.returnTitle")}</h4>
      <p className="production-hint">{t("production.detail.materials.returnHint")}</p>
      <label className="filter-chip">
        <span className="filter-chip-label">{t("production.detail.materials.returnQty")}</span>
        <input
          value={qty}
          inputMode="decimal"
          required
          data-testid="material-return-qty"
          onChange={(event) => {
            setQty(event.target.value);
          }}
        />
      </label>
      <span className="production-hint">{issue.uom}</span>
      {validationKey !== undefined ? (
        <p className="form-error" role="alert">
          {t(validationKey)}
        </p>
      ) : null}
      {/* The closed-order refusal is the TAB's to word (see the header): it
          closes this form, so it is never rendered in it twice. */}
      {returnIssue.isError && !isClosedReturnRefusal(returnIssue.error) ? (
        <p className="form-error" role="alert">
          {returnErrorMessage(t, returnIssue.error)}
        </p>
      ) : null}
      <div className="form-actions wo-transitions">
        {/* type="submit" explicitly — Button defaults to type="button" and this
            control submits the return <form>. */}
        <Button
          type="submit"
          variant="primary"
          busy={returnIssue.isPending}
          busyLabel={t("production.common.saving")}
          data-testid="material-return-submit"
        >
          {t("production.detail.materials.returnSubmit")}
        </Button>
        <Button onClick={onDone}>{t("production.common.cancel")}</Button>
      </div>
    </form>
  );
}

/** §5 row 30, POSITIVE branch only (D-076): the form cannot express a return. */
function IssueForm({
  workOrderId,
  lines,
  operations,
  productName,
  onDone,
}: {
  workOrderId: string;
  lines: readonly WorkOrderMaterialLine[];
  operations: readonly WorkOrderOperation[];
  productName: (id: string) => string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const { term } = useTerm();
  const issue = useCreateMaterialIssue();
  const intent = useIntentKey();

  const componentIds = useMemo(
    () => Array.from(new Set(lines.map((line) => line.component_product_id))),
    [lines],
  );

  const [componentId, setComponentId] = useState(componentIds[0] ?? "");
  const [qty, setQty] = useState("");
  const [uom, setUom] = useState(
    lines.find((line) => line.component_product_id === (componentIds[0] ?? ""))?.uom ?? "",
  );
  const [opId, setOpId] = useState("");
  const [validationKey, setValidationKey] = useState<string | undefined>(undefined);

  // The tenant's units, plus the unit this line is planned in — which is the
  // selected value and must be selectable before (and even if) the catalogue
  // list answers.
  const uoms = useUoms();
  const uomOptions = useMemo(() => {
    const codes = new Set<string>();
    if (uom.length > 0) codes.add(uom);
    for (const line of lines) codes.add(line.uom);
    for (const row of uoms.data?.items ?? []) codes.add(row.code);
    return [...codes];
  }, [uom, lines, uoms.data]);

  return (
    <form
      className="production-panel"
      aria-label={t("production.detail.materials.issueTitle")}
      onSubmit={(event) => {
        event.preventDefault();
        if (componentId.length === 0) return;
        // POSITIVE ONLY: a zero or negative quantity is refused here with the
        // stated sentence, not sent to collect the server's 422.
        if (!isValidQtyInput(qty, { positive: true })) {
          setValidationKey("production.detail.materials.qtyPositive");
          return;
        }
        setValidationKey(undefined);
        const body: MaterialIssueCreateRequest = {
          component_product_id: componentId,
          qty: qty.trim(),
          uom,
        };
        if (opId.length > 0) body.wo_operation_id = opId;
        issue.mutate(
          { workOrderId, body, idempotencyKey: intent.keyFor(JSON.stringify(body)) },
          {
            onSuccess: () => {
              intent.settle();
              onDone();
            },
            onError: (error) => {
              intent.settle(error);
            },
          },
        );
      }}
    >
      <h4>{t("production.detail.materials.issueTitle")}</h4>
      <label className="filter-chip">
        <span className="filter-chip-label">{term("material")}</span>
        <select
          value={componentId}
          onChange={(event) => {
            const next = event.target.value;
            setComponentId(next);
            const line = lines.find((l) => l.component_product_id === next);
            if (line !== undefined) setUom(line.uom);
          }}
        >
          {componentIds.map((id) => (
            <option key={id} value={id}>
              {productName(id)}
            </option>
          ))}
        </select>
      </label>
      <label className="filter-chip">
        <span className="filter-chip-label">{t("production.detail.materials.qty")}</span>
        <input
          value={qty}
          inputMode="decimal"
          required
          onChange={(event) => {
            setQty(event.target.value);
          }}
        />
      </label>
      {/* ONE UoM CONTROL FOR ONE CONCEPT (surface review F12). This was a
          FREE-TEXT box while the create-WO modal two clicks away offered a
          select of the tenant's units for the same field — so the same
          question was asked two ways, and the typed answer ("kgs", "Kg", a
          unit the tenant never defined) is one the server can only refuse
          (`UOM_NOT_CONVERTIBLE` / 422). The plan line's own unit stays the
          default and is always offered, even before the catalogue list
          answers, so the common path is one keystroke shorter than it was. */}
      <label className="filter-chip">
        <span className="filter-chip-label">{t("production.detail.materials.uom")}</span>
        <select
          value={uom}
          required
          onChange={(event) => {
            setUom(event.target.value);
          }}
        >
          {uomOptions.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </select>
      </label>
      <label className="filter-chip">
        <span className="filter-chip-label">{t("production.detail.materials.forStep")}</span>
        <select
          value={opId}
          onChange={(event) => {
            setOpId(event.target.value);
          }}
        >
          <option value="">{t("production.detail.materials.noStep")}</option>
          {operations.map((op) => (
            <option key={op.id} value={op.id}>
              {op.seq_no} — {op.name}
            </option>
          ))}
        </select>
      </label>
      {validationKey !== undefined ? (
        <p className="form-error" role="alert">
          {t(validationKey)}
        </p>
      ) : null}
      {issue.isError ? (
        <p className="form-error" role="alert">
          {productionErrorMessage(t, issue.error)}
        </p>
      ) : null}
      <div className="form-actions wo-transitions">
        <Button
          type="submit"
          variant="primary"
          busy={issue.isPending}
          busyLabel={t("production.common.saving")}
        >
          {t("production.detail.materials.submit")}
        </Button>
        <Button onClick={onDone}>{t("production.common.cancel")}</Button>
      </div>
    </form>
  );
}
